/**
 * Attachment Encryption Module
 *
 * Per-attachment encryption key (BEK) management for file attachments.
 * Each attachment is encrypted with its own random AES-GCM key. The BEK is
 * wrapped (encrypted) with the document's DEK for storage.
 *
 * On DEK rotation, only the small wrapped-BEK values are re-encrypted.
 * The attachment files on disk are untouched.
 *
 * Format:
 *   encrypted attachment = IV (12 bytes) + AES-GCM ciphertext
 *   wrapped BEK    = IV (12 bytes) + AES-GCM ciphertext of 32-byte BEK
 */

/**
 * Generate a random BEK, encrypt file bytes with it, and wrap the BEK with the doc DEK.
 *
 * @param {CryptoKey} dek - Document encryption key (AES-GCM, for wrapping BEK)
 * @param {ArrayBuffer} plaintext - File contents
 * @returns {Promise<{ encrypted: Uint8Array, wrappedBek: Uint8Array }>}
 */
export async function encryptAttachment(dek, plaintext) {
  // Generate random 32-byte BEK
  const bekRaw = crypto.getRandomValues(new Uint8Array(32));

  // Import BEK for encryption
  const bekKey = await crypto.subtle.importKey(
    'raw', bekRaw, { name: 'AES-GCM' }, true, ['encrypt']
  );

  // Encrypt file with BEK
  const blobIv = crypto.getRandomValues(new Uint8Array(12));
  const blobCiphertext = await crypto.subtle.encrypt(
    { name: 'AES-GCM', iv: blobIv },
    bekKey,
    plaintext,
  );

  // Combine IV + ciphertext
  const encrypted = new Uint8Array(12 + blobCiphertext.byteLength);
  encrypted.set(blobIv, 0);
  encrypted.set(new Uint8Array(blobCiphertext), 12);

  // Wrap BEK with DEK
  const wrappedBek = await wrapBek(dek, bekRaw);

  // bekRaw: only for a file waiting to be sent (rewrapped if the map's key
  // changes first); never stored otherwise.
  return { encrypted, wrappedBek, bekRaw };
}

/**
 * Unwrap the BEK with the doc DEK, then decrypt the attachment.
 *
 * @param {CryptoKey} dek - Document encryption key
 * @param {Uint8Array|ArrayBuffer} encrypted - IV (12) + ciphertext (BEK-encrypted attachment)
 * @param {Uint8Array|ArrayBuffer} wrappedBek - IV (12) + ciphertext (DEK-encrypted BEK)
 * @returns {Promise<ArrayBuffer>} Decrypted file contents
 */
export async function decryptAttachment(dek, encrypted, wrappedBek) {
  // Unwrap BEK
  const bekRaw = await unwrapBek(dek, wrappedBek);

  // Import BEK for decryption
  const bekKey = await crypto.subtle.importKey(
    'raw', bekRaw, { name: 'AES-GCM' }, false, ['decrypt']
  );

  // Split IV + ciphertext
  const data = encrypted instanceof Uint8Array ? encrypted : new Uint8Array(encrypted);
  const iv = data.slice(0, 12);
  const ciphertext = data.slice(12);

  // Decrypt
  return crypto.subtle.decrypt(
    { name: 'AES-GCM', iv },
    bekKey,
    ciphertext,
  );
}

/**
 * Re-wrap a BEK from oldDek to newDek (for DEK rotation).
 *
 * @param {CryptoKey} oldDek - Previous document encryption key
 * @param {CryptoKey} newDek - New document encryption key
 * @param {Uint8Array|ArrayBuffer} wrappedBek - BEK wrapped with oldDek
 * @returns {Promise<Uint8Array>} BEK wrapped with newDek
 */
export async function rekeyBek(oldDek, newDek, wrappedBek) {
  const bekRaw = await unwrapBek(oldDek, wrappedBek);
  return wrapBek(newDek, bekRaw);
}

// ============================================================
// Filename encryption (AES-GCM with doc DEK)
//
// Filenames are encrypted directly with the doc DEK (not per-blob BEK) so
// that listAttachments can show names without unwrapping every blob's BEK.
// DEK rotation re-encrypts filenames alongside the BEK rewrap loop —
// negligible extra work per blob since filenames are small.
//
// Format: IV (12 bytes) + AES-GCM ciphertext of UTF-8 bytes
// ============================================================

/**
 * Encrypt a filename string with the doc DEK.
 * @param {CryptoKey} dek
 * @param {string} filename
 * @returns {Promise<Uint8Array>} IV (12) + ciphertext
 */
export async function encryptFilename(dek, filename) {
  const iv = crypto.getRandomValues(new Uint8Array(12));
  const plaintext = new TextEncoder().encode(filename || '');
  const ciphertext = await crypto.subtle.encrypt(
    { name: 'AES-GCM', iv },
    dek,
    plaintext,
  );
  const result = new Uint8Array(12 + ciphertext.byteLength);
  result.set(iv, 0);
  result.set(new Uint8Array(ciphertext), 12);
  return result;
}

/**
 * Decrypt a filename. Returns null if input is empty/invalid (treat as legacy
 * blob with no stored filename — caller should show a placeholder).
 * @param {CryptoKey} dek
 * @param {Uint8Array|ArrayBuffer|null|undefined} encryptedFilename
 * @returns {Promise<string|null>}
 */
export async function decryptFilename(dek, encryptedFilename) {
  if (!encryptedFilename) return null;
  const data = encryptedFilename instanceof Uint8Array
    ? encryptedFilename
    : new Uint8Array(encryptedFilename);
  if (data.byteLength < 13) return null;
  const iv = data.slice(0, 12);
  const ciphertext = data.slice(12);
  try {
    const plaintext = await crypto.subtle.decrypt(
      { name: 'AES-GCM', iv },
      dek,
      ciphertext,
    );
    return new TextDecoder().decode(plaintext);
  } catch {
    return null;
  }
}

/**
 * Re-encrypt a filename from oldDek to newDek (for DEK rotation).
 * @param {CryptoKey} oldDek
 * @param {CryptoKey} newDek
 * @param {Uint8Array|ArrayBuffer} encryptedFilename
 * @returns {Promise<Uint8Array|null>} Null if input couldn't be decrypted.
 */
export async function rekeyFilename(oldDek, newDek, encryptedFilename) {
  const name = await decryptFilename(oldDek, encryptedFilename);
  if (name === null) return null;
  return encryptFilename(newDek, name);
}

/**
 * Wrap a raw BEK with a DEK.
 * @param {CryptoKey} dek
 * @param {Uint8Array} bekRaw - 32-byte raw BEK
 * @returns {Promise<Uint8Array>} IV (12) + ciphertext
 */
export async function wrapBek(dek, bekRaw) {
  const iv = crypto.getRandomValues(new Uint8Array(12));
  const ciphertext = await crypto.subtle.encrypt(
    { name: 'AES-GCM', iv },
    dek,
    bekRaw,
  );
  const result = new Uint8Array(12 + ciphertext.byteLength);
  result.set(iv, 0);
  result.set(new Uint8Array(ciphertext), 12);
  return result;
}

/**
 * Unwrap a BEK from its DEK-encrypted form.
 * @param {CryptoKey} dek
 * @param {Uint8Array|ArrayBuffer} wrappedBek - IV (12) + ciphertext
 * @returns {Promise<Uint8Array>} Raw 32-byte BEK
 */
async function unwrapBek(dek, wrappedBek) {
  const data = wrappedBek instanceof Uint8Array ? wrappedBek : new Uint8Array(wrappedBek);
  const iv = data.slice(0, 12);
  const ciphertext = data.slice(12);
  const raw = await crypto.subtle.decrypt(
    { name: 'AES-GCM', iv },
    dek,
    ciphertext,
  );
  return new Uint8Array(raw);
}
